import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { certificateService } from "../services/api/certificate.service";
import { serviceChargeService } from "../services/api/serviceCharge.service";
import { ApplicationCategory } from "../types/certificate.types";
import { WorkflowId } from "../types/workflow.types";
import type { County } from "../types/certificate.types";
import type { ServiceCharge } from "../types/serviceCharge.types";
import { getAccessToken } from "../api/apiClient";
import { showErrorToast } from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";

/**
 * Self-service "start a new application" form (shell-free).
 * Marriage or Divorce is determined by the route. Applications created here are always
 * ProcessAtHeadquarters = false and use the county workflow.
 */
const PortalApply = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDivorce = location.pathname.includes("/divorce");
  const typeLabel = isDivorce ? "Divorce" : "Marriage";

  // The date cannot be earlier than today.
  const todayStr = (() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
  })();

  const [counties, setCounties] = useState<County[]>([]);
  const [serviceCharge, setServiceCharge] = useState<ServiceCharge | null>(null);
  const [countyId, setCountyId] = useState(0);
  const [place, setPlace] = useState("");
  const [date, setDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!getAccessToken()) {
      navigate("/");
      return;
    }
    const load = async () => {
      try {
        const [countyData, charge] = await Promise.all([
          certificateService.getCounties(),
          serviceChargeService.getServiceChargeByCategoryId(isDivorce ? 2 : 1),
        ]);
        // "Central Office" is the HQ entry — not a real county for self-service.
        setCounties(
          (Array.isArray(countyData) ? countyData : []).filter(
            (c) => (c.name || "").trim().toLowerCase() !== "central office",
          ),
        );
        setServiceCharge(charge ?? null);
      } catch {
        setServiceCharge(null);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!countyId || !place.trim() || !date) {
      showErrorToast("Please complete all fields.");
      return;
    }
    if (date < todayStr) {
      showErrorToast(
        `The ${typeLabel.toLowerCase()} date cannot be earlier than today.`,
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        category: isDivorce
          ? ApplicationCategory.Divorce
          : ApplicationCategory.Marriage,
        countyId,
        placeOfWedding: place.trim(),
        dateOfWedding: date,
        processAtHeadquarters: false,
        workflowId: isDivorce
          ? WorkflowId.CountyDivorcesWorkflow
          : WorkflowId.CountyMarriagesWorkflow,
        ...(serviceCharge
          ? {
              lraPayment: serviceCharge.lraPayment,
              operationalCost: serviceCharge.operationalCost,
              currencyId: serviceCharge.currencyId,
            }
          : {}),
      };
      const created =
        await certificateService.createCertificateApplication(payload);
      const base = isDivorce ? "/portal/divorce/apply" : "/portal/marriages/apply";
      navigate(`${base}/${created.id}`);
    } catch (err) {
      const message =
        err instanceof Error && err.message ? err.message : GENERIC_ERROR_MESSAGE;
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="portal-detail-body">
      <header className="portal-detail-topbar">
        <button
          type="button"
          className="portal-btn portal-btn--ghost"
          onClick={() => navigate("/portal")}
        >
          ← My Applications
        </button>
        <span className="portal-detail-title">New {typeLabel} Application</span>
        <span />
      </header>

      <main className="main-content">
        <div className="user-detail-page" style={{ maxWidth: 640, margin: "0 auto" }}>
          <div className="portal-start" style={{ marginTop: 24 }}>
            <h2>Start a New {typeLabel} Application</h2>
            <p>
              Provide the basic details to begin. You'll add the
              {isDivorce ? " plaintiff and defendant" : " groom and bride"} details
              and documents on the next screen.
            </p>
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-field">
                <select
                  id="county"
                  value={countyId}
                  onChange={(e) => setCountyId(Number(e.target.value))}
                  required
                >
                  <option value={0} disabled>
                    Select County
                  </option>
                  {counties.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <label htmlFor="county">County</label>
              </div>
              <div className="form-field">
                <input
                  id="place"
                  type="text"
                  placeholder=" "
                  value={place}
                  onChange={(e) => setPlace(e.target.value)}
                  required
                />
                <label htmlFor="place">
                  {isDivorce ? "Place of Divorce" : "Place of Marriage"}
                </label>
              </div>
              <div className="form-field">
                <input
                  id="date"
                  type="date"
                  placeholder=" "
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  min={todayStr}
                  required
                />
                <label htmlFor="date">
                  {isDivorce ? "Divorce Filed Date" : "Date of Marriage"}
                </label>
              </div>
              <button
                className="primary-btn"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Creating…" : "Create & Continue"}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PortalApply;
